import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Option,
  Spinner,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePractices } from "apps/project-iq/hooks/usePractices";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { PeoplePicker } from "components/fields/peoplePicker/PeoplePicker";
import { CustomOption } from "components/fields/peoplePicker/PeoplePicker.types";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
  useSharedDropdownStyles,
} from "theme/sharedStyles";
import { toUserOption } from "utils/generalUtils";
import { usePortfoliosPracticesAccessStyles } from "./PortfoliosPracticesAccess.styles";
import { IPracticeViewerAccessDialogProps } from "./PortfoliosPracticesAccess.types";

export const PracticeViewerAccessDialog = ({
  isOpen,
  isSaving,
  editingItem = null,
  onSave,
  onDismiss,
}: IPracticeViewerAccessDialogProps): JSX.Element => {
  const styles = usePortfoliosPracticesAccessStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();
  const dropdownStyles = useSharedDropdownStyles();

  const { practices, isLoading: isPracticesLoading } = usePractices();

  const [selectedUser, setSelectedUser] = useState<CustomOption[]>([]);
  const [practiceIds, setPracticeIds] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setSelectedUser(
        editingItem
          ? [
              toUserOption(
                editingItem.user.userId,
                editingItem.user.displayName,
              ),
            ]
          : [],
      );
      setPracticeIds(editingItem?.practiceIds ?? []);
      setFieldErrors({});
    }
  }, [isOpen, editingItem]);

  const sortedPractices = useMemo(
    () => [...practices].sort((a, b) => a.label.localeCompare(b.label)),
    [practices],
  );

  const handleSelectedUserChange = useCallback(
    (options: CustomOption[]): void => {
      const lastSelected = options.length ? [options[options.length - 1]] : [];
      setSelectedUser(lastSelected);
      setFieldErrors((prev) => ({ ...prev, user: "" }));
    },
    [],
  );

  const handlePracticesChange = useCallback(
    (newPracticeIds: string[]): void => {
      setPracticeIds(newPracticeIds);
      setFieldErrors((prev) => ({ ...prev, practices: "" }));
    },
    [],
  );

  const handleSave = async (): Promise<void> => {
    const errors: Record<string, string> = {};
    if (selectedUser.length === 0) errors.user = "A user is required";
    if (practiceIds.length === 0)
      errors.practices = "At least one practice is required";
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    try {
      await onSave({
        user: {
          userId: selectedUser[0].id ?? selectedUser[0].key,
          displayName: selectedUser[0].text,
        },
        practiceIds,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save";
      setFieldErrors((prev) => ({ ...prev, general: errorMessage }));
    }
  };

  const isSaveDisabled =
    isSaving || selectedUser.length === 0 || practiceIds.length === 0;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, d) => !d.open && !isSaving && onDismiss()}
    >
      <DialogSurface className={contentStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={onDismiss}
                disabled={isSaving}
              />
            }
          >
            {editingItem
              ? "Edit practice viewer access"
              : "Add practice viewer access"}
          </DialogTitle>
          <DialogContent>
            <div className={contentStyles.formContainer}>
              {isSaving && (
                <div className={contentStyles.formLoadingOverlay}>
                  <Spinner label="Saving practice viewer access..." />
                </div>
              )}
              {fieldErrors.general && (
                <div role="alert" className={styles.errorAlert}>
                  {fieldErrors.general}
                </div>
              )}
              <Field
                label="User"
                required
                validationState={fieldErrors.user ? "error" : "none"}
                validationMessage={fieldErrors.user}
                className={styles.dialogField}
              >
                <PeoplePicker
                  selectedOptions={selectedUser}
                  handleSelectedItems={handleSelectedUserChange}
                  hasOnlyOurUsers
                  hasSingleSelection
                  disabled={isSaving}
                />
              </Field>
              <Field
                label="Practices"
                required
                validationState={fieldErrors.practices ? "error" : "none"}
                validationMessage={fieldErrors.practices}
                className={styles.dialogField}
              >
                <Dropdown
                  multiselect
                  placeholder={
                    isPracticesLoading
                      ? "Loading practices..."
                      : "Select practices..."
                  }
                  value={practiceIds
                    .map(
                      (id) =>
                        practices.find((practice) => practice.id === id)?.label,
                    )
                    .filter((label): label is string => Boolean(label))
                    .join(", ")}
                  selectedOptions={practiceIds}
                  onOptionSelect={(_e, data) =>
                    handlePracticesChange(data.selectedOptions)
                  }
                  className={dropdownStyles.dropdown}
                  listbox={{ className: dropdownStyles.listbox }}
                  positioning="below"
                  disabled={isSaving || isPracticesLoading}
                >
                  {sortedPractices.map((practice) => (
                    <Option
                      key={practice.id}
                      value={practice.id}
                      text={practice.label}
                    >
                      {practice.label}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            </div>
          </DialogContent>
        </DialogBody>
        <div className={footerStyles.dialogFooter}>
          <DialogFooterAction
            icon={<CheckmarkRegular aria-hidden="true" />}
            onClick={() => void handleSave()}
            disabled={isSaveDisabled}
            tooltip="Save"
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};
